import { useState, useEffect, Fragment } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicQuotesService } from '../../services/api';
import { formatMoney } from '../../utils/pricing';
import './Booking.css';

const STATUS_LABEL = { draft: 'Awaiting your reply', sent: 'Awaiting your reply', accepted: 'Accepted', declined: 'Declined', expired: 'Expired' };

function fmtDay(ymd) {
  if (!ymd) return '';
  return new Date(`${ymd}T12:00:00+10:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}
function fmtWhen(iso) {
  return new Date(iso).toLocaleDateString('en-AU', { timeZone: 'Australia/Brisbane', day: 'numeric', month: 'long', year: 'numeric' });
}
function fmtQty(q) {
  const n = Number(q) || 0;
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
}

// Areas in drawing order with each cut-out nested under the area(s) it takes
// from (the server sets `cuts` / `cut_of`); a cut-out that overlaps no area
// stays a top-level row.
function nestAreas(areas) {
  const byN = new Map(areas.map((a) => [a.n, a]));
  return areas
    .filter((a) => !a.is_cut || !(a.cut_of || []).length)
    .map((a) => ({ area: a, cuts: (a.cuts || []).map((c) => ({ ...c, area: byN.get(c.n) })).filter((c) => c.area) }));
}

// The marked-up aerial view; shown only once the image has actually loaded,
// so a quote whose map cannot be served just has no map section.
function MapImage({ src }) {
  const [state, setState] = useState('loading'); // loading | ok | failed
  if (state === 'failed') return null;
  return (
    <figure className="pq-map" hidden={state !== 'ok'}>
      <img src={src} alt="Aerial view with the measured areas marked" onLoad={() => setState('ok')} onError={() => setState('failed')} />
      <figcaption className="text-muted">Imagery © Google. Shapes are drawn on aerial imagery that may be older than the property.</figcaption>
    </figure>
  );
}

// The customer's view of a quote, reached from the link in their email.
export default function QuoteView() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [name, setName] = useState('');
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [justAccepted, setJustAccepted] = useState(false);

  useEffect(() => {
    setLoading(true);
    publicQuotesService.get(token).then((r) => {
      if (r.success) { setQuote(r.quote); setName(r.quote.customer?.name || ''); }
      else setNotFound(true);
      setLoading(false);
    });
  }, [token]);

  async function accept() {
    setError('');
    if (!name.trim()) { setError('Please enter your name to accept.'); return; }
    if (!window.confirm(`Accept quote ${quote.quote_number} for ${formatMoney(quote.total)}?`)) return;
    setBusy(true);
    const r = await publicQuotesService.accept(token, name.trim());
    setBusy(false);
    if (r.success) { setQuote(r.quote); setJustAccepted(true); }
    else setError(r.error || 'Could not accept the quote.');
  }
  async function decline() {
    setError(''); setBusy(true);
    const r = await publicQuotesService.decline(token, reason.trim());
    setBusy(false);
    if (r.success) { setQuote(r.quote); setDeclining(false); }
    else setError(r.error || 'Could not update the quote.');
  }

  if (loading) return <div className="booking-card"><div className="loading-spinner"></div></div>;
  if (notFound) return (
    <div className="booking-card booking-done">
      <h1>Quote not found</h1>
      <p className="text-muted">This link may have expired or is incorrect.</p>
      <Link className="btn btn-primary" to="/book">Make a booking</Link>
    </div>
  );

  const open = quote.status === 'sent' || quote.status === 'draft';
  const b = quote.business || {};

  return (
    <div className="booking-wizard">
      {error && <div className="booking-error">{error}</div>}
      <div className="booking-card pq">
        <div className="pq-head">
          <div>
            <p className="pq-kicker">Quote {quote.quote_number}</p>
            <h1>{quote.title || 'Your quote'}</h1>
            {quote.customer && (
              <p className="text-muted">Prepared for {quote.customer.name}{quote.customer.address ? ` · ${quote.customer.address}` : ''}</p>
            )}
            {quote.measured && (
              <p className="pq-measured"><span>{quote.measured.label}</span><strong>{quote.measured.text}</strong></p>
            )}
          </div>
          <span className={`status-badge status-${quote.status}`}>{STATUS_LABEL[quote.status] || quote.status}</span>
        </div>

        {quote.intro && <p className="pq-intro">{quote.intro}</p>}

        <div className="pq-items">
          {quote.items.map((it, i) => (
            <div key={i} className="pq-item">
              <span className="pq-item-desc">
                {it.description}
                {Number(it.qty) !== 1 && !it.unit ? <span className="text-muted"> × {fmtQty(it.qty)}</span> : null}
              </span>
              <span className="pq-item-amt">{formatMoney(it.line_total)}</span>
            </div>
          ))}
        </div>
        <div className="pq-totals">
          {quote.includes_gst && (
            <>
              <span>Subtotal (ex GST)</span><span>{formatMoney(quote.subtotal)}</span>
              <span>GST (10%)</span><span>{formatMoney(quote.gst)}</span>
            </>
          )}
          <strong>Total{quote.includes_gst ? ' (inc GST)' : ''}</strong><strong>{formatMoney(quote.total)}</strong>
        </div>
        {quote.show_areas && quote.areas?.length > 0 && (
          <div className="pq-areas">
            <h2>Areas measured</h2>
            <div className="pq-areas-list">
              {nestAreas(quote.areas).map(({ area: a, cuts }) => (
                <Fragment key={a.n}>
                  <div className={`pq-area ${a.is_cut ? 'pq-area-cut' : ''} ${String(a.size || '').length > 12 ? 'pq-area-long' : ''}`}>
                    <span className="pq-area-swatch" style={{ background: a.colour }}>{quote.show_map ? a.marker : ''}</span>
                    <span className="pq-area-label">{a.label}{a.surface && !a.is_cut && a.surface.toLowerCase() !== String(a.label || '').toLowerCase() ? <span className="text-muted"> · {a.surface}</span> : null}{a.is_cut ? <span className="text-muted"> · excluded</span> : null}</span>
                    <span className="pq-area-size">{a.size}</span>
                  </div>
                  {cuts.map((c) => (
                    <div key={c.n} className="pq-area pq-area-cut pq-area-sub">
                      <span className="pq-area-swatch" style={{ background: c.area.colour }}>{quote.show_map ? c.area.marker : ''}</span>
                      <span className="pq-area-label">{c.area.label}<span className="text-muted"> · excluded</span></span>
                      <span className="pq-area-size">{c.size}</span>
                    </div>
                  ))}
                </Fragment>
              ))}
              {quote.measured && (
                <div className="pq-area pq-area-total">
                  <span className="pq-area-swatch pq-area-swatch-none"></span>
                  <span className="pq-area-label">{quote.measured.label}{quote.areas.some((a) => a.is_cut) ? <span className="text-muted"> · net of cut-outs</span> : null}</span>
                  <span className="pq-area-size">{quote.measured.text}</span>
                </div>
              )}
            </div>
            <p className="text-muted pq-areas-note">Sizes are measured from aerial imagery and are a guide to the scope of work.</p>
          </div>
        )}
        {quote.show_map && <MapImage src={publicQuotesService.mapUrl(token)} />}
        {quote.valid_until && open && <p className="text-muted pq-valid">Valid until {fmtDay(quote.valid_until)}</p>}
        {quote.terms && <div className="pq-terms"><h2>Terms</h2><p>{quote.terms}</p></div>}

        {open && !declining && (
          <div className="pq-accept">
            <h2>Happy to go ahead?</h2>
            <div className="form-group">
              <label htmlFor="pq_name">Your name</label>
              <input id="pq_name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="done-actions">
              <button className="btn btn-primary" onClick={accept} disabled={busy}>{busy ? 'One moment…' : 'Accept quote'}</button>
              <button className="btn btn-outline" onClick={() => setDeclining(true)} disabled={busy}>Not right now</button>
            </div>
          </div>
        )}
        {open && declining && (
          <div className="pq-accept">
            <h2>No worries</h2>
            <div className="form-group">
              <label htmlFor="pq_reason">Anything we should know? (optional)</label>
              <textarea id="pq_reason" rows="2" value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            <div className="done-actions">
              <button className="btn btn-danger" onClick={decline} disabled={busy}>Decline quote</button>
              <button className="btn btn-outline" onClick={() => setDeclining(false)} disabled={busy}>Back</button>
            </div>
          </div>
        )}
        {quote.status === 'accepted' && (
          <div className="pq-accept pq-accepted">
            <div className="done-check">✓</div>
            <h2>{justAccepted ? 'Thanks — you are all set!' : 'Quote accepted'}</h2>
            <p className="text-muted">
              Accepted{quote.accepted_name ? ` by ${quote.accepted_name}` : ''}{quote.accepted_at ? ` on ${fmtWhen(quote.accepted_at)}` : ''}.
              {' '}We will be in touch to lock in a time{b.phone ? `, or call ${b.phone}` : ''}.
            </p>
            <div className="done-actions"><Link className="btn btn-primary" to="/book">Book a time now</Link></div>
          </div>
        )}
        {quote.status === 'declined' && (
          <div className="pq-accept">
            <h2>Thanks for letting us know</h2>
            <p className="text-muted">Changed your mind? {b.phone ? `Give us a call on ${b.phone}.` : 'Get in touch any time.'}</p>
          </div>
        )}
        {quote.status === 'expired' && (
          <div className="pq-accept">
            <h2>This quote has expired</h2>
            <p className="text-muted">{b.phone ? `Call us on ${b.phone} and we will refresh it for you.` : 'Get in touch and we will refresh it for you.'}</p>
          </div>
        )}

        <p className="pq-footer text-muted">
          <a href={publicQuotesService.pdfUrl(token)} target="_blank" rel="noopener noreferrer">Download PDF</a>
          {' '}· {b.name}{b.abn ? ` · ABN ${b.abn}` : ''}{b.email ? ` · ${b.email}` : ''}
        </p>
      </div>
    </div>
  );
}
